import type { AxiosError } from 'axios';
import useNotifications from '../../../../../_lib/notifications/useNotifications';
import { manageAPIError } from '../../../../../utils';
import type {
  CoverageOption,
  QuoteOptionsResult,
} from '../../../../../api/submissions/submission-coverages.types';
import { optionLabel } from './optionCartRow';
import { useQuoteOptions } from './useQuoteOptions';
import {
  useRequestQuotesForOptions,
  type QuoteRequest,
} from './useRequestQuotesForOptions';

type FailedOption = { readonly optionId: string; readonly error: string };

/** What a request that sent the submission to underwriting, instead of quoting it, tells the person. */
export const SENT_TO_UNDERWRITING = 'Sent to underwriting for custom quoting';

/**
 * The rows that came back with an error, how many options got a quote, and why the submission went to underwriting
 * instead, or null when it did not.
 */
export const summarizeQuoteResults = (
  results: QuoteOptionsResult[]
): { created: number; failed: FailedOption[]; referral: string | null } => {
  const failed = results.flatMap((result) =>
    result.error ? [{ optionId: result.optionId, error: result.error }] : []
  );
  return {
    created: results.filter((result) => !result.error && result.quoteId).length,
    failed,
    referral: results.find((result) => result.referral)?.referral ?? null,
  };
};

const nameOf = (options: CoverageOption[], optionId: string): string => {
  const option = options.find((candidate) => candidate.id === optionId);
  return option ? optionLabel(option) : 'Option';
};

/**
 * Quotes the chosen options and reports each failure by option, or the referral when the
 * submission went to underwriting instead. Resolves to the number of quotes created, so a caller
 * redirects only when at least one exists. A request that fails outright shows one error and
 * resolves to zero.
 */
export const useSubmitQuoteOptions = (submissionId: string) => {
  const notifications = useNotifications();
  const request = useRequestQuotesForOptions(submissionId);
  const optionsQuery = useQuoteOptions(submissionId);

  /** `context` is what the cart submit sent besides the options: agent, team, renewal, agency id. */
  const submit = async (
    optionIds: string[],
    context: Omit<QuoteRequest, 'optionIds'> = {}
  ): Promise<number> => {
    try {
      const results = await request.mutateAsync({ ...context, optionIds });
      const { created, failed, referral } = summarizeQuoteResults(results);
      const options = optionsQuery.data?.coverageOptions ?? [];
      failed.forEach((failure) =>
        notifications.show({
          type: 'error',
          title: `${nameOf(options, failure.optionId)} could not be quoted`,
          body: failure.error,
        })
      );
      if (referral !== null) {
        notifications.show({
          type: 'info',
          title: SENT_TO_UNDERWRITING,
          body: referral,
        });
      }
      if (created > 0) {
        notifications.show({
          type: 'success',
          title: 'Quotes requested successfully',
        });
      }
      return created;
    } catch (error) {
      notifications.show({
        type: 'error',
        title: 'Unable to request quotes at this time',
        body: manageAPIError(error as AxiosError),
      });
      return 0;
    }
  };

  return { submit, isLoading: request.isLoading };
};
